// SidebarBottomBlock — the account block pinned to the bottom of the rail:
// Deals · Organization · Settings · Admin Ops · FAQs · Log out.
//
// Identical in all four rail states (landing included) so Settings → Appearance, the theme control,
// is never stranded behind a workflow choice. Groups with children collapse; groups without render
// as plain links. Purely presentational — gates are evaluated here from props, never fetched.
import { LogOut } from 'lucide-react';
import { ApprovalsBadge } from './SidebarStatusBadges';
import SidebarGroup, { type SidebarGroupChild } from './SidebarGroup';
import {
  type SidebarBottomChild,
  type SidebarBottomGroup,
  type SidebarGate,
  BOTTOM_GROUPS,
} from './sidebarNav';
import { useAccess } from '@/hooks/useAccess';
import { featureRolledOutTo } from '@/auth/features';

export interface SidebarBottomBlockProps {
  /** Permission map lookups, resolved by the parent from usePermissions(). */
  canManageOrg: boolean;
  canApprove: boolean;
  canPeAdmin: boolean;
  /** Org slug, or null when the user has no org nav. */
  orgSlug: string | null;
  /** Pending-approvals count on the Admin Ops → Approvals child. */
  showApprovalsBadge: boolean;
  onNavigate: () => void;
  onLogout: () => void;
}

export default function SidebarBottomBlock({
  canManageOrg,
  canApprove,
  canPeAdmin,
  orgSlug,
  showApprovalsBadge,
  onNavigate,
  onLogout,
}: SidebarBottomBlockProps) {
  // Rollout check, read here rather than added to the three-boolean prop drill above.
  const { tier } = useAccess();
  const rolledOut = (child: SidebarBottomChild): boolean =>
    !child.feature || featureRolledOutTo(child.feature, tier);

  const passes = (gate: SidebarGate | undefined): boolean => {
    switch (gate) {
      case undefined:
        return true;
      case 'org:manage_members':
        return canManageOrg;
      case 'admin:approve':
        return canApprove;
      case 'pe:admin':
        return canPeAdmin;
      case 'orgNav':
        return orgSlug !== null;
      default:
        return false;
    }
  };

  const resolve = (to: SidebarBottomChild['to']): string =>
    typeof to === 'function' ? to(orgSlug ?? '') : to;

  const groupVisible = (group: SidebarBottomGroup): boolean =>
    group.gateAny ? group.gateAny.some(passes) : passes(group.gate);

  return (
    <div className="sidebar-account">
      <div className="sidebar-account-divider" />

      {BOTTOM_GROUPS.filter(groupVisible).map((group) => {
        const children: SidebarGroupChild[] = group.children
          .filter((c) => passes(c.gate) && rolledOut(c))
          .map((c) => {
            const to = resolve(c.to);
            return {
              to,
              label: c.label,
              isActive: (loc) => loc.pathname === to || loc.pathname.startsWith(`${to}/`),
              badge: c.approvalsBadge && showApprovalsBadge ? <ApprovalsBadge /> : undefined,
            };
          });

        // `overviewTo: null` means "the first child the user can actually see", so the parent row
        // never points at a route that would 403. Falls back to the group's own route when set.
        const overviewTo =
          group.overviewTo !== null ? resolve(group.overviewTo) : (children[0]?.to ?? null);
        if (overviewTo === null) return null;

        return (
          <SidebarGroup
            key={group.key}
            icon={group.icon}
            label={group.label}
            overviewTo={overviewTo}
            isOverviewActive={(loc) => loc.pathname === overviewTo}
            items={children}
            onNavigate={onNavigate}
          />
        );
      })}

      <button
        type="button"
        className="sidebar-nav-link sidebar-nav-link--muted sidebar-logout-btn btn-unstyled"
        onClick={onLogout}
        aria-label="Log out"
      >
        <LogOut className="sidebar-icon" aria-hidden="true" />
        <span className="sidebar-nav-label">Log out</span>
      </button>
    </div>
  );
}
